<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisMoneySnapshot from 'dashboard/components-next/LaisMoneySnapshot.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  compactLedgerEvents,
  divergenceReasonKey,
  financialStatusTone,
  firstRefundableIntent,
  hasRefundableBalance,
  paymentLedgerFor,
  reconciliationTone,
} from '../utils/reservationHoldLedger';

const props = defineProps({
  hold: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  // Feature disabled (403) — render a calm unavailable state, never a dead CTA.
  unavailable: { type: Boolean, default: false },
  canManage: { type: Boolean, default: false },
  actionInFlight: { type: String, default: '' },
  error: { type: String, default: '' },
});

const emit = defineEmits(['confirm', 'release', 'cancel', 'refund']);

/**
 * ReservationHoldPanel — FE-AUD-75. Reserva/sinal ledger for a single hold,
 * with the server-derived settlement reconciliation (required vs settled vs
 * outstanding) surfaced explicitly so a hold never looks "active" without a
 * confirmed payment. The provider `idempotency_key` and raw payloads are never
 * rendered. Emits confirm/release/cancel; the host owns the API call.
 */
const HOLD_TONES = {
  active: 'info',
  confirmed: 'positive',
  released: 'neutral',
  expired: 'warning',
  canceled: 'neutral',
};

const { t } = useI18n();

const status = computed(() => String(props.hold?.status || '').toLowerCase());
const statusTone = computed(() => HOLD_TONES[status.value] || 'neutral');
const ledger = computed(() => paymentLedgerFor(props.hold));
const settlement = computed(() => props.hold?.settlement || null);
const isDivergent = computed(
  () =>
    ledger.value?.divergent ||
    (settlement.value &&
      settlement.value.settled === false &&
      Number(settlement.value.settled_amount) > 0)
);
const isOpen = computed(() => ['active', 'confirmed'].includes(status.value));
const ledgerEvents = computed(() => compactLedgerEvents(props.hold));
const refundableIntent = computed(() => firstRefundableIntent(props.hold));
const canRefund = computed(
  () => props.canManage && hasRefundableBalance(props.hold)
);
const canConfirm = computed(
  () =>
    ledger.value?.actions?.can_confirm ??
    (Number(settlement.value?.outstanding_amount || 0) === 0 &&
      !isDivergent.value)
);

const actions = computed(() => {
  if (!props.canManage) return [];
  if (status.value === 'active') return ['confirm', 'cancel'];
  if (status.value === 'confirmed') return ['release'];
  return [];
});

const actionLabelKey = {
  confirm: 'PROPERTY_OFFERS.RESERVATION.CONFIRM',
  release: 'PROPERTY_OFFERS.RESERVATION.RELEASE',
  cancel: 'PROPERTY_OFFERS.RESERVATION.CANCEL',
};

const run = action => {
  if (action === 'confirm') emit('confirm');
  else if (action === 'release') emit('release');
  else if (action === 'cancel') emit('cancel');
};

const refund = () => {
  if (!refundableIntent.value) return;
  emit('refund', {
    intentId: refundableIntent.value.id,
    amount: refundableIntent.value.balance,
  });
};
</script>

<template>
  <section
    class="reservation-hold-panel flex w-full flex-col gap-4 rounded-lais border border-lais-border-soft bg-lais-bg-card p-4 shadow-lais-card sm:p-5"
    :aria-busy="loading ? 'true' : undefined"
    :aria-label="t('PROPERTY_OFFERS.RESERVATION.TITLE')"
  >
    <header class="flex flex-wrap items-center justify-between gap-3">
      <h2
        class="flex items-center gap-2 font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
      >
        <span
          class="i-lucide-hand-coins size-5 shrink-0 text-lais-primary"
          aria-hidden="true"
        />
        {{ t('PROPERTY_OFFERS.RESERVATION.TITLE') }}
      </h2>
      <LaisStatusBadge
        v-if="hold"
        :label="t(`PROPERTY_OFFERS.RESERVATION.STATUS.${status.toUpperCase()}`)"
        :tone="statusTone"
      />
    </header>

    <!-- Loading -->
    <div v-if="loading" class="grid gap-3" aria-hidden="true">
      <div class="h-4 w-1/3 animate-pulse rounded bg-lais-bg-soft" />
      <div class="h-12 w-full animate-pulse rounded-lais bg-lais-bg-soft" />
    </div>

    <!-- Feature unavailable -->
    <div
      v-else-if="unavailable"
      class="flex items-center gap-2 rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-4 text-lais-body-sm text-lais-text-muted"
    >
      <span class="i-lucide-info size-4 shrink-0" aria-hidden="true" />
      {{ t('PROPERTY_OFFERS.RESERVATION.UNAVAILABLE') }}
    </div>

    <!-- Empty: no hold -->
    <div
      v-else-if="!hold"
      class="flex flex-col items-start gap-1 rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-5"
    >
      <p class="text-lais-body-sm text-lais-text-body">
        {{ t('PROPERTY_OFFERS.RESERVATION.EMPTY') }}
      </p>
    </div>

    <template v-else>
      <p
        v-if="error"
        class="inline-flex items-start gap-1.5 rounded-lais-sm bg-lais-danger-50 px-3 py-2 text-lais-body-sm text-lais-danger"
        role="alert"
      >
        <span
          class="i-lucide-triangle-alert mt-px size-4 shrink-0"
          aria-hidden="true"
        />
        {{ error }}
      </p>

      <div class="flex flex-wrap items-end justify-between gap-4">
        <LaisMoneySnapshot
          :amount="hold.amount"
          :currency="hold.currency || 'BRL'"
          :label="t('PROPERTY_OFFERS.RESERVATION.AMOUNT')"
          emphasis="lg"
        />
        <LaisMoneySnapshot
          v-if="settlement"
          :amount="settlement.outstanding_amount"
          :currency="hold.currency || 'BRL'"
          :label="t('PROPERTY_OFFERS.RESERVATION.OUTSTANDING')"
          :tone="
            isDivergent
              ? 'warning'
              : Number(settlement.outstanding_amount) === 0
                ? 'positive'
                : 'neutral'
          "
          emphasis="md"
        />
      </div>

      <!-- Reconciliation -->
      <dl
        v-if="settlement"
        class="reservation-hold-panel__settlement grid grid-cols-2 gap-x-4 gap-y-2 rounded-lais-sm bg-lais-bg-soft px-3 py-3 text-lais-body-sm"
      >
        <div>
          <dt class="text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.RESERVATION.REQUIRED') }}
          </dt>
          <dd class="font-semibold tabular-nums text-lais-text">
            {{ settlement.required_amount }}
          </dd>
        </div>
        <div>
          <dt class="text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.RESERVATION.SETTLED') }}
          </dt>
          <dd class="font-semibold tabular-nums text-lais-text">
            {{ settlement.settled_amount }}
          </dd>
        </div>
        <div>
          <dt class="text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.RESERVATION.INTENTS') }}
          </dt>
          <dd class="font-semibold tabular-nums text-lais-text">
            {{ settlement.intents_count }}
          </dd>
        </div>
      </dl>

      <div
        v-if="ledger"
        class="grid gap-3 rounded-lais-sm border border-lais-border-soft p-3 text-lais-body-sm"
      >
        <div class="flex flex-wrap items-center gap-2">
          <LaisStatusBadge
            :label="
              t(
                `PROPERTY_OFFERS.RESERVATION.FINANCIAL_STATUS.${String(
                  ledger.financial_status
                ).toUpperCase()}`
              )
            "
            :tone="financialStatusTone(ledger.financial_status)"
            size="sm"
          />
          <LaisStatusBadge
            :label="
              t(
                `PROPERTY_OFFERS.RESERVATION.RECONCILIATION_STATUS.${String(
                  ledger.reconciliation_status
                ).toUpperCase()}`
              )
            "
            :tone="reconciliationTone(ledger.reconciliation_status)"
            size="sm"
          />
          <span class="text-lais-body-xs text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.RESERVATION.PAYLOADS_REDACTED') }}
          </span>
        </div>

        <ul v-if="ledger.divergence_reasons?.length" class="grid gap-1">
          <li
            v-for="reason in ledger.divergence_reasons"
            :key="reason"
            class="inline-flex items-start gap-1.5 text-lais-body-xs text-lais-text"
          >
            <span
              class="i-lucide-triangle-alert mt-px size-3.5 shrink-0 text-lais-warning"
              aria-hidden="true"
            />
            {{
              t(
                `PROPERTY_OFFERS.RESERVATION.DIVERGENCE_REASON.${divergenceReasonKey(
                  reason
                )}`
              )
            }}
          </li>
        </ul>

        <div v-if="ledger.receipts?.length" class="grid gap-2">
          <h3 class="text-lais-body-xs font-semibold text-lais-text-muted">
            {{ t('PROPERTY_OFFERS.RESERVATION.RECEIPTS') }}
          </h3>
          <div
            v-for="receipt in ledger.receipts"
            :key="receipt.id"
            class="flex flex-wrap items-center justify-between gap-2 rounded-lais-sm bg-lais-bg-soft px-3 py-2"
          >
            <span class="font-medium text-lais-text">
              {{
                t(
                  `PROPERTY_OFFERS.RESERVATION.RECEIPT_KIND.${String(
                    receipt.receipt_kind
                  ).toUpperCase()}`
                )
              }}
            </span>
            <span class="font-mono text-lais-body-xs text-lais-text-muted">
              {{ String(receipt.checksum).slice(0, 12) }}
            </span>
          </div>
        </div>

        <ol v-if="ledgerEvents.length" class="grid gap-2">
          <li
            v-for="event in ledgerEvents"
            :key="event.id"
            class="flex flex-wrap items-center justify-between gap-2 border-t border-lais-border-soft pt-2 first:border-t-0 first:pt-0"
          >
            <span class="text-lais-body-xs text-lais-text">
              {{
                t(
                  `PROPERTY_OFFERS.RESERVATION.LEDGER_EVENT.${String(
                    event.kind
                  ).toUpperCase()}`
                )
              }}
            </span>
            <span class="text-lais-body-xs tabular-nums text-lais-text-muted">
              {{ event.amount }} {{ event.currency }}
            </span>
          </li>
        </ol>
      </div>

      <p
        v-if="isDivergent"
        class="inline-flex items-start gap-1.5 rounded-lais-sm bg-lais-warning/15 px-3 py-2 text-lais-body-sm text-lais-text"
      >
        <span
          class="i-lucide-triangle-alert mt-px size-4 shrink-0"
          aria-hidden="true"
        />
        {{ t('PROPERTY_OFFERS.RESERVATION.DIVERGENCE') }}
      </p>

      <p
        v-if="hold.release_reason"
        class="text-lais-body-xs text-lais-text-muted"
      >
        {{
          t('PROPERTY_OFFERS.RESERVATION.RELEASE_REASON', {
            reason: hold.release_reason,
          })
        }}
      </p>

      <!-- Actions -->
      <div v-if="actions.length || canRefund" class="flex flex-wrap gap-2">
        <LaisPrimaryButton
          v-for="action in actions"
          :key="action"
          size="sm"
          :variant="action === 'confirm' ? 'primary' : 'ghost'"
          :is-loading="actionInFlight === action"
          :disabled="action === 'confirm' && !canConfirm"
          @click="run(action)"
        >
          {{ t(actionLabelKey[action]) }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="canRefund"
          size="sm"
          variant="ghost"
          :is-loading="actionInFlight === 'refund'"
          @click="refund"
        >
          {{ t('PROPERTY_OFFERS.RESERVATION.REFUND') }}
        </LaisPrimaryButton>
      </div>
      <p v-else-if="!isOpen" class="text-lais-body-xs text-lais-text-muted">
        {{ t('PROPERTY_OFFERS.RESERVATION.TERMINAL') }}
      </p>
    </template>
  </section>
</template>

<style scoped>
.reservation-hold-panel {
  container-type: inline-size;
}

@container (min-width: 28rem) {
  .reservation-hold-panel__settlement {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
